import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/app/AppShell";
import {
  STUB_INVOICES,
  BILLING_LETTERHEAD,
  type StubInvoice,
} from "../../lib/stubs";
import { useAuth } from "../../lib/auth";

// Rechnungen · Download & Vorschau — all stub. The preview card renders one
// invoice (the first in the filtered list) using the real PEALO-equivalent
// letterhead from /impressum (tino24/7 GmbH, HRB 256376 B, DE365576955).

const YEAR_FILTERS = ["Alle", "2026", "2025"] as const;
type YearFilter = (typeof YEAR_FILTERS)[number];

export default function AppInvoices() {
  const { user } = useAuth();
  const [year, setYear] = useState<YearFilter>("Alle");

  const filtered = useMemo(
    () =>
      year === "Alle"
        ? STUB_INVOICES
        : STUB_INVOICES.filter((i) => i.year === parseInt(year, 10)),
    [year]
  );

  const yearlyTotal = useMemo(() => {
    const totals = filtered
      .map((i) => parseFloat(i.total.replace(",", ".").replace(/[^\d.]/g, "")))
      .filter((n) => !isNaN(n));
    const sum = totals.reduce((a, b) => a + b, 0);
    return sum.toFixed(2).replace(".", ",");
  }, [filtered]);

  const preview = filtered[0];

  return (
    <AppShell
      eyebrow="Konto · Rechnungen"
      title={
        <>
          Ihre <em>Rechnungen</em>
        </>
      }
    >
      <div style={{ padding: "0 22px 90px" }}>
        {/* Summary card */}
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 14,
            padding: "16px 18px",
            marginBottom: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <div>
              <div className="t-caps" style={{ fontSize: 10 }}>
                Gesamt {year === "Alle" ? "alle Jahre" : year}
              </div>
              <div
                style={{
                  fontFamily: "var(--f-display)",
                  fontStyle: "italic",
                  fontSize: 32,
                  color: "var(--ink)",
                  lineHeight: 1,
                  marginTop: 4,
                }}
              >
                {yearlyTotal}{" "}
                <span style={{ fontSize: 18, color: "var(--ink-faint)" }}>€</span>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="t-caps" style={{ fontSize: 10 }}>
                Steuerlich
              </div>
              <button
                type="button"
                // TODO(Phase C): generate yearly export PDF.
                style={{
                  marginTop: 6,
                  padding: "8px 14px",
                  borderRadius: 999,
                  background: "transparent",
                  border: "1px solid rgba(43,26,15,0.20)",
                  fontFamily: "var(--f-ui)",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--ink)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                }}
              >
                <DownloadIcon stroke="currentColor" />
                Jahresübersicht
              </button>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px dashed rgba(43,26,15,0.14)",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 12.5,
              color: "var(--ink-soft)",
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--sage)"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Alle Rechnungen sind bezahlt · keine offenen Beträge.</span>
          </div>
        </div>

        {/* Year segmented pills */}
        <div
          role="tablist"
          aria-label="Zeitraum"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
          }}
        >
          {YEAR_FILTERS.map((y) => {
            const active = year === y;
            return (
              <button
                key={y}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => setYear(y)}
                style={{
                  padding: "7px 14px",
                  borderRadius: 999,
                  background: active ? "var(--ink)" : "transparent",
                  color: active ? "var(--paper-50)" : "var(--ink-soft)",
                  border: active ? "none" : "1px solid rgba(43,26,15,0.18)",
                  fontFamily: "var(--f-ui)",
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {y}
              </button>
            );
          })}
        </div>

        {/* Invoice list */}
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 12,
            overflow: "hidden",
            marginBottom: 12,
          }}
        >
          {filtered.length === 0 ? (
            <div
              style={{
                padding: "20px",
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 14,
                color: "var(--ink-faint)",
                textAlign: "center",
              }}
            >
              Keine Rechnungen in diesem Zeitraum.
            </div>
          ) : (
            filtered.map((inv, i) => (
              <InvoiceRow
                key={inv.nr}
                inv={inv}
                last={i === filtered.length - 1}
              />
            ))
          )}
        </div>

        {/* Bulk ZIP */}
        <button
          type="button"
          // TODO(Phase C): server-side ZIP of all invoices.
          className="btn-secondary"
          style={{ width: "100%", boxSizing: "border-box", marginBottom: 24 }}
        >
          <DownloadIcon stroke="currentColor" />
          Alle als ZIP herunterladen
        </button>

        {/* Live invoice preview */}
        {preview && (
          <section style={{ marginBottom: 18 }}>
            <div className="t-caps" style={{ marginBottom: 10 }}>
              Vorschau · {preview.nr}
            </div>
            <div
              style={{
                background: "var(--paper-50)",
                border: "1px solid rgba(43,26,15,0.10)",
                borderRadius: 8,
                padding: "22px 22px 20px",
                boxShadow: "0 14px 28px -12px rgba(43,26,15,0.18)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Bezahlt stamp */}
              {preview.status === "paid" && (
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 18,
                    right: -18,
                    transform: "rotate(-12deg)",
                    border: "2px solid var(--sage)",
                    color: "var(--sage)",
                    fontFamily: "var(--f-ui)",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.20em",
                    textTransform: "uppercase",
                    padding: "4px 26px",
                    borderRadius: 4,
                    opacity: 0.75,
                    background: "var(--paper-50)",
                  }}
                >
                  Bezahlt
                </div>
              )}

              {/* Letterhead */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  paddingBottom: 14,
                  borderBottom: "1px solid rgba(43,26,15,0.10)",
                }}
              >
                <div>
                  <div
                    style={{
                      fontFamily: "var(--f-display)",
                      fontStyle: "italic",
                      fontSize: 20,
                      color: "var(--ink)",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Vitanara
                    <span style={{ color: "var(--oxblood)" }}>.</span>
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--f-body)",
                      fontStyle: "italic",
                      fontSize: 10.5,
                      color: "var(--ink-faint)",
                      marginTop: 4,
                      lineHeight: 1.4,
                    }}
                  >
                    {BILLING_LETTERHEAD.company}
                    <br />
                    {BILLING_LETTERHEAD.street} · {BILLING_LETTERHEAD.cityZip}
                    <br />
                    {BILLING_LETTERHEAD.hrb} · {BILLING_LETTERHEAD.ustId}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="t-caps" style={{ fontSize: 9 }}>
                    Rechnung
                  </div>
                  <div
                    style={{
                      fontFamily: "ui-monospace, monospace",
                      fontSize: 12,
                      color: "var(--ink)",
                      marginTop: 4,
                    }}
                  >
                    {preview.nr}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--f-body)",
                      fontStyle: "italic",
                      fontSize: 11,
                      color: "var(--ink-faint)",
                      marginTop: 2,
                    }}
                  >
                    {preview.date}
                  </div>
                </div>
              </div>

              {/* Bill-to */}
              <div style={{ paddingTop: 14, paddingBottom: 14 }}>
                <div className="t-caps" style={{ fontSize: 9, marginBottom: 4 }}>
                  Rechnung an
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-display)",
                    fontWeight: 500,
                    fontSize: 14,
                    color: "var(--ink)",
                  }}
                >
                  {user?.displayName || "Buchhaltung"}
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 12,
                    color: "var(--ink-faint)",
                    lineHeight: 1.5,
                    marginTop: 2,
                  }}
                >
                  {user?.email || "—"}
                </div>
              </div>

              {/* Line items */}
              <div
                style={{
                  paddingTop: 10,
                  borderTop: "1px solid rgba(43,26,15,0.10)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  marginBottom: 8,
                }}
              >
                <InvLine n={preview.title} sum="149,00 €" />
                {preview.promoCode && (
                  <InvLine
                    n={`Aktionscode ${preview.promoCode} (−15 %)`}
                    sum="− 22,35 €"
                    muted
                  />
                )}
                <InvLine
                  n="Zwischensumme netto"
                  sum={preview.items.netSubtotal}
                  muted
                  thin
                />
                <InvLine n="USt. 19 %" sum={preview.items.vat} muted thin />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  paddingTop: 10,
                  borderTop: "1.5px solid var(--ink)",
                  fontFamily: "var(--f-display)",
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: "var(--ink)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                  }}
                >
                  Gesamt
                </span>
                <span
                  style={{
                    fontSize: 22,
                    color: "var(--oxblood)",
                    fontStyle: "italic",
                  }}
                >
                  {preview.total}
                </span>
              </div>

              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: "1px dashed rgba(43,26,15,0.14)",
                  fontFamily: "var(--f-body)",
                  fontStyle: "italic",
                  fontSize: 11,
                  color: "var(--ink-faint)",
                  lineHeight: 1.5,
                }}
              >
                Zahlung eingegangen am {preview.date} ·{" "}
                {preview.items.paymentNote}.
                <br />
                Reverse-Charge-Verfahren findet keine Anwendung.
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                // TODO(Phase C): wire signed-URL download.
                className="btn-primary"
                style={{ flex: 1 }}
              >
                <DownloadIcon stroke="currentColor" />
                PDF herunterladen
              </button>
              <button
                type="button"
                // TODO(Phase C): trigger transactional email with PDF attached.
                style={{
                  padding: "12px 14px",
                  borderRadius: 999,
                  background: "transparent",
                  border: "1px solid rgba(43,26,15,0.20)",
                  fontFamily: "var(--f-ui)",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--ink)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" />
                </svg>
                E-Mail
              </button>
            </div>
          </section>
        )}

        {/* Helper */}
        <div
          style={{
            background: "rgba(168,122,58,0.10)",
            border: "1px solid rgba(168,122,58,0.30)",
            borderRadius: 10,
            padding: "12px 14px",
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--oxblood)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0, marginTop: 1 }}
            aria-hidden
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <div
            style={{
              fontFamily: "var(--f-body)",
              fontSize: 13,
              lineHeight: 1.5,
              color: "var(--ink-soft)",
            }}
          >
            Brauchen Sie eine Rechnungskorrektur oder eine geänderte Anschrift?{" "}
            <Link
              to="/app/buchhaltung"
              style={{
                color: "var(--oxblood)",
                fontWeight: 600,
                textDecoration: "underline",
                textUnderlineOffset: 2,
              }}
            >
              An Buchhaltung schreiben →
            </Link>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function InvoiceRow({ inv, last }: { inv: StubInvoice; last: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        borderBottom: last ? "none" : "1px solid rgba(43,26,15,0.06)",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 38,
          height: 46,
          borderRadius: 4,
          background: "var(--paper-200)",
          border: "1px solid rgba(43,26,15,0.14)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          position: "relative",
          gap: 2,
          paddingTop: 5,
        }}
      >
        <div style={{ width: 18, height: 1, background: "rgba(43,26,15,0.30)" }} />
        <div style={{ width: 14, height: 1, background: "rgba(43,26,15,0.20)" }} />
        <div style={{ width: 16, height: 1, background: "rgba(43,26,15,0.20)" }} />
        <div
          style={{
            marginTop: 4,
            fontFamily: "var(--f-ui)",
            fontSize: 7.5,
            fontWeight: 700,
            color: "var(--oxblood)",
            letterSpacing: "0.12em",
          }}
        >
          PDF
        </div>
        {/* Eselsohr */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderBottom: "6px solid var(--paper-50)",
          }}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 500,
            fontSize: 14,
            color: "var(--ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {inv.title}
        </div>
        <div
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 12,
            color: "var(--ink-faint)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {inv.sub}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            marginTop: 3,
          }}
        >
          <span
            style={{
              padding: "1px 6px",
              borderRadius: 3,
              background: "rgba(95,107,79,0.18)",
              color: "#3F4A33",
              fontFamily: "var(--f-ui)",
              fontSize: 8.5,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            {inv.status === "paid" ? "Bezahlt" : "Offen"}
          </span>
          <span
            style={{
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 11,
              color: "var(--ink-faint)",
            }}
          >
            {inv.date}
          </span>
          <span
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 10,
              color: "var(--ink-faint)",
            }}
          >
            · {inv.nr}
          </span>
        </div>
      </div>

      <div
        style={{
          fontFamily: "var(--f-display)",
          fontSize: 15,
          color: "var(--ink)",
          flexShrink: 0,
          textAlign: "right",
        }}
      >
        {inv.total}
      </div>

      <button
        type="button"
        aria-label={`Rechnung ${inv.nr} herunterladen`}
        // TODO(Phase C): wire signed-URL download.
        style={{
          width: 34,
          height: 34,
          borderRadius: 8,
          background: "var(--oxblood)",
          border: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          cursor: "pointer",
        }}
      >
        <DownloadIcon stroke="var(--paper-50)" />
      </button>
    </div>
  );
}

function InvLine({
  n,
  sum,
  muted,
  thin,
}: {
  n: string;
  sum: string;
  muted?: boolean;
  thin?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: thin ? "2px 0" : "4px 0",
        fontFamily: "var(--f-body)",
        fontSize: thin ? 11.5 : 13,
        color: muted ? "var(--ink-faint)" : "var(--ink)",
        fontStyle: muted && thin ? "italic" : "normal",
      }}
    >
      <span>{n}</span>
      <span style={{ fontFamily: "var(--f-display)" }}>{sum}</span>
    </div>
  );
}

function DownloadIcon({ stroke = "currentColor" }: { stroke?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  );
}
